import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import StaffNavbar from '../components/StaffNavbar';
import BackButton from '../components/BackButton';
import Icon from '../components/Icon';
import apiService from '../services/api';
import '../styles/StaffPages.css';

const RaffleDraw = () => {
  const { raffleId } = useParams();
  const [raffle, setRaffle] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drawing, setDrawing] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });

  const fetchRaffleAndEntries = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch raffle details
      const raffleData = await apiService.getRaffle(raffleId);
      setRaffle(raffleData);

      // Fetch raffle entries
      const entriesData = await apiService.getRaffleEntries(raffleId);
      setEntries(entriesData.results || entriesData || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching raffle data:', err);
      setError('Failed to load raffle data');
    } finally {
      setLoading(false);
    }
  }, [raffleId]);

  useEffect(() => {
    fetchRaffleAndEntries();
  }, [fetchRaffleAndEntries]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedEntries = React.useMemo(() => {
    let sortableEntries = [...entries];

    // If raffle is completed, sort winner to top first
    if (raffle?.status === 'completed' && raffle?.winner) {
      const winnerId = raffle.winner.id;
      const winnerEntry = sortableEntries.find(entry => entry.user?.id === winnerId);
      const otherEntries = sortableEntries.filter(entry => entry.user?.id !== winnerId);

      if (winnerEntry) {
        sortableEntries = [winnerEntry, ...otherEntries];
      }
    }

    // Then apply regular sorting if specified
    if (sortConfig.key !== null) {
      // If winner is at top, sort only the non-winner entries
      const hasWinnerAtTop = raffle?.status === 'completed' && raffle?.winner;
      const startIndex = hasWinnerAtTop ? 1 : 0;
      const entriesToSort = sortableEntries.slice(startIndex);

      entriesToSort.sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.key) {
          case 'firstName':
            aValue = a.user?.first_name || '';
            bValue = b.user?.first_name || '';
            break;
          case 'lastName':
            aValue = a.user?.last_name || '';
            bValue = b.user?.last_name || '';
            break;
          case 'email':
            aValue = a.user?.email || '';
            bValue = b.user?.email || '';
            break;
          case 'entries':
            aValue = a.entry_count || 0;
            bValue = b.entry_count || 0;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });

      if (hasWinnerAtTop) {
        sortableEntries = [sortableEntries[0], ...entriesToSort];
      } else {
        sortableEntries = entriesToSort;
      }
    }

    return sortableEntries;
  }, [entries, sortConfig, raffle]);

  const handleDrawWinner = async () => {
    if (drawing || entries.length === 0) return;

    try {
      setDrawing(true);

      // 3-second UI delay for animation only; the random pick is performed
      // server-side by the /draw endpoint using a cryptographically secure RNG.
      await new Promise(resolve => setTimeout(resolve, 3000));

      await apiService.drawRaffleWinner(raffleId);

      await fetchRaffleAndEntries();

    } catch (error) {
      console.error('Error drawing winner:', error);
      setError('Failed to draw winner. Please try again.');
    } finally {
      setDrawing(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <StaffNavbar activeSection="raffles" />
        <div className="admin-main">
          <div className="admin-content">
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div>Loading raffle data...</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard">
        <StaffNavbar activeSection="raffles" />
        <div className="admin-main">
          <div className="admin-content">
            <div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
              <div>{error}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <div className="admin-dashboard">
        <StaffNavbar activeSection="raffles" />
      <div className="admin-main">
        <div className="admin-content">
          <BackButton text="Back to Raffles" to="/staff/raffles" />
          <h1 className="staff-page-header">Raffle Draw: {raffle?.title}</h1>
          <p className="staff-page-subheader">View and entries for this raffle drawing.</p>

          {sortedEntries.length > 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'flex-start',
              marginTop: '24px',
              marginBottom: '32px'
            }}>
              {raffle?.status === 'completed' && raffle?.winner ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '16px 32px',
                  backgroundColor: '#E8F5E8',
                  border: '2px solid #4CAF50',
                  borderRadius: '100px',
                  color: '#2E7D32',
                  fontSize: '16px',
                  fontWeight: '700'
                }}>
                  <div style={{
                    filter: 'brightness(0) saturate(100%) invert(32%) sepia(85%) saturate(449%) hue-rotate(82deg) brightness(94%) contrast(95%)',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <Icon name="checkmark" size={18} />
                  </div>
                  Winner: {raffle.winner.first_name} {raffle.winner.last_name} ({raffle.winner.email})
                </div>
              ) : raffle?.status !== 'completed' ? (
                <button
                  className="staff-page-button"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '16px 32px',
                    fontSize: '16px',
                    fontWeight: '700',
                    backgroundColor: drawing ? '#cccccc' : '#00C8FF',
                    border: 'none',
                    borderRadius: '100px',
                    color: '#000',
                    cursor: drawing ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s ease',
                    opacity: drawing ? 0.6 : 1
                  }}
                  onMouseEnter={(e) => !drawing && (e.target.style.backgroundColor = '#00B0E6')}
                  onMouseLeave={(e) => !drawing && (e.target.style.backgroundColor = '#00C8FF')}
                  onClick={handleDrawWinner}
                  disabled={drawing}
                >
                  {drawing ? (
                    <div style={{
                      width: '18px',
                      height: '18px',
                      border: '2px solid #666',
                      borderTop: '2px solid #000',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                  ) : (
                    <Icon name="ticket" size={18} />
                  )}
                  {drawing ? 'Drawing Winner...' : 'Draw Winning Ticket'}
                </button>
              ) : null}
            </div>
          )}

          <h3 style={{
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: '700',
            lineHeight: 'normal',
            marginTop: '32px',
            marginBottom: '4px',
          }}>
            Raffle Entries ({sortedEntries.length})
          </h3>
          <p style={{
            color: 'var(--Black, #000)',
            fontSize: '12px',
            fontStyle: 'normal',
            fontWeight: '340',
            lineHeight: 'normal',
            opacity: '0.5',
            marginTop: '0',
            marginBottom: '16px'
          }}>
            All users who have entered this raffle and their entry counts
          </p>

          <div style={{
            display: 'flex',
            padding: '18px',
            alignItems: 'center',
            alignSelf: 'stretch',
            borderRadius: '12px 12px 0 0',
            background: '#F8F8F8',
            backdropFilter: 'blur(22.5px)'
          }}>
            <div
              onClick={() => handleSort('firstName')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '25%',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer'
              }}>
              First Name
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('lastName')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '25%',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer'
              }}>
              Last Name
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('email')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '35%',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer'
              }}>
              Email
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('entries')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '15%',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer'
              }}>
              Entries
              <Icon name="up-down" size={12} />
            </div>
          </div>

          <div style={{ marginBottom: '8px' }}></div>

          {sortedEntries.length === 0 ? (
            <div style={{
              display: 'flex',
              width: '100%',
              maxWidth: '1270px',
              height: '59px',
              padding: '18px',
              alignItems: 'center',
              borderRadius: '12px',
              background: '#F8F8F8',
              marginBottom: '8px',
              justifyContent: 'center',
              fontFamily: '"Baikal Book"',
              fontSize: '14px',
              color: '#6b7280'
            }}>
              No entries found for this raffle.
            </div>
          ) : (
            sortedEntries.map((entry, index) => {
              const isWinner = raffle?.status === 'completed' && raffle?.winner && entry.user?.id === raffle.winner.id;

              return (
                <div key={entry.id || index} style={{
                  display: 'flex',
                  width: '100%',
                  maxWidth: '1270px',
                  height: '59px',
                  padding: '18px',
                  alignItems: 'center',
                  borderRadius: '12px',
                  background: isWinner ? '#E8F5E8' : '#F8F8F8',
                  border: isWinner ? '2px solid #4CAF50' : 'none',
                  marginBottom: '8px',
                  boxShadow: isWinner ? '0 2px 8px rgba(76, 175, 80, 0.2)' : 'none'
                }}>
                <div style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  width: '25%',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {entry.user?.first_name || 'N/A'}
                </div>
                <div style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  width: '25%',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {entry.user?.last_name || 'N/A'}
                </div>
                <div style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  width: '35%',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {entry.user?.email || 'N/A'}
                </div>
                <div style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  width: '15%',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {entry.entry_count || 1}
                </div>
              </div>
              );
            })
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default RaffleDraw;